import type {
  ApproveSalaryAdvanceDTO,
  CreateSalaryAdvanceDTO,
  ListSalaryAdvancesQuery,
  ListSalaryAdvancesResult,
  RejectSalaryAdvanceDTO,
  SalaryAdvanceDetail,
} from "@/modules/hr/salary-advance/domain/contracts/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = `${config.apiUrl}/hr/salary-advance`;

export const salaryAdvanceApi = {
  // ─── Admin ───
  list(query: ListSalaryAdvancesQuery): Promise<ListSalaryAdvancesResult> {
    const params = new URLSearchParams();
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.status) params.set("status", query.status);
    if (query.employeeId) params.set("employeeId", query.employeeId);
    if (query.dateFrom) params.set("dateFrom", query.dateFrom);
    if (query.dateTo) params.set("dateTo", query.dateTo);
    const q = params.toString();
    return fetcher.get<ListSalaryAdvancesResult>(q ? `${base}?${q}` : base);
  },

  getById(id: string): Promise<SalaryAdvanceDetail> {
    return fetcher.get<SalaryAdvanceDetail>(`${base}/${id}`);
  },

  approve(id: string, dto: ApproveSalaryAdvanceDTO): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/${id}/approve`, dto);
  },

  reject(id: string, dto: RejectSalaryAdvanceDTO): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/${id}/reject`, dto);
  },

  markPaid(id: string): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/${id}/mark-paid`, {});
  },

  createForEmployee(dto: CreateSalaryAdvanceDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(base, dto);
  },

  // ─── Employee Self-Service ───
  listMine(
    query: Omit<ListSalaryAdvancesQuery, "employeeId">,
  ): Promise<ListSalaryAdvancesResult> {
    const params = new URLSearchParams();
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.status) params.set("status", query.status);
    const q = params.toString();
    return fetcher.get<ListSalaryAdvancesResult>(
      q ? `${base}/me/requests?${q}` : `${base}/me/requests`,
    );
  },

  getMyById(id: string): Promise<SalaryAdvanceDetail> {
    return fetcher.get<SalaryAdvanceDetail>(`${base}/me/requests/${id}`);
  },

  createRequest(
    dto: Omit<CreateSalaryAdvanceDTO, "employeeId">,
  ): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(`${base}/me/requests`, dto);
  },

  cancelRequest(id: string): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(
      `${base}/me/requests/${id}/cancel`,
      {},
    );
  },
};
